import React, { useEffect, useState } from "react";
import "./MyLearning.css";
import { open_api_with_cookies } from "../../Utils/network";
import CustomAlert from "../AlertDialog/CustomAlert";
import { addAlert, removeAlert } from "../Dialog/helper";
import { useNavigate, useLocation } from "react-router-dom";
import {
  NoPackagesContainer,
  NoPackagesImage,
  NoPackagesText,
} from "../../Components/Dialog/PaymentDialogStyles";
import emptyBox from "../../Assets/Logo_icons/emptybox.svg";
import WhatsAppButton from "../Button/WhatsAppButton";
import ChallengeCard from "./ChallengeCard";

const ITEMS_PER_PAGE = 12;

const MyLearning = () => {
  // Tab state
  const [activeTab, setActiveTab] = useState("workshops");

  // Workshop states (existing)
  const [currentPage, setCurrentPage] = useState(1);
  const [sortOption, setSortOption] = useState(null);
  const [filterOption, setFilterOption] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [workshops, setWorkshops] = useState([]);
  const [totalPages, setTotalPages] = useState(0);

  // Challenge states (simplified - no sorting)
  const [challengeCurrentPage, setChallengeCurrentPage] = useState(1);
  const [challengeSearchQuery, setChallengeSearchQuery] = useState("");
  const [challenges, setChallenges] = useState([]);
  const [challengeTotalPages, setChallengeTotalPages] = useState(0);

  // Common states
  const [alerts, setAlerts] = useState([]);
  const [showAlert, setShowAlert] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  // Existing workshop fetch function (unchanged)
  const fetchWorkshops = async (page = 1) => {
    const bearerToken = localStorage.getItem("bearerToken");
    const baseUrl = "/workshop/purchased_list";

    const queryParams = new URLSearchParams();

    queryParams.append("sort_by", sortOption || "recently_purchased");

    if (filterOption) {
      queryParams.append("type", filterOption);
    }

    if (searchQuery) {
      queryParams.append("search", searchQuery);
    }

    queryParams.append("page", page);

    const url = `${baseUrl}${queryParams.toString() ? "?" + queryParams.toString() : ""}`;

    try {
      const response = await open_api_with_cookies.get(url);
      if (
        response.status === 200 ||
        response.status === 201 ||
        response.status === 204
      ) {
        const fetchedWorkshops = response?.data?.data || [];

        setWorkshops(fetchedWorkshops);

        const maxPages = response?.data?.max_pages || 1;
        setTotalPages(maxPages);
      }
    } catch (error) {
      console.error("Error fetching workshops:", error);
    }
  };

  // Simplified challenge fetch function (no sorting)
  const fetchChallenges = async (page = 1) => {
    const bearerToken = localStorage.getItem("bearerToken");
    const baseUrl = "/challenge/purchase/my-purchases/";

    const queryParams = new URLSearchParams();

    if (challengeSearchQuery) {
      queryParams.append("search", challengeSearchQuery);
    }

    queryParams.append("page", page);

    const url = `${baseUrl}${queryParams.toString() ? "?" + queryParams.toString() : ""}`;

    try {
      const response = await open_api_with_cookies.get(url);
      if (
        response.status === 200 ||
        response.status === 201 ||
        response.status === 204
      ) {
        const fetchedChallenges = response?.data?.data || [];
        setChallenges(fetchedChallenges);

        const maxPages = response?.data?.max_pages || 1;
        setChallengeTotalPages(maxPages);
      }
    } catch (error) {
      console.error("Error fetching challenges:", error);
    }
  };

  // Effects
  useEffect(() => {
    if (activeTab === "workshops") {
      fetchWorkshops(currentPage);
    }
  }, [sortOption, filterOption, searchQuery, currentPage, activeTab]);

  useEffect(() => {
    if (activeTab === "challenges") {
      fetchChallenges(challengeCurrentPage);
    }
  }, [challengeSearchQuery, challengeCurrentPage, activeTab]);

  // Tab handlers
  const handleTabChange = (tab) => {
    setActiveTab(tab);
  };

  // Workshop handlers (existing)
  const handleSortChange = (e) => {
    setSortOption(e.target.value);
    setCurrentPage(1);
  };

  const handleFilterChange = (e) => {
    setFilterOption(e.target.value);
    setCurrentPage(1);
  };

  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
    setCurrentPage(1);
  };

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };

  // Challenge handlers (simplified)
  const handleChallengeSearchChange = (e) => {
    setChallengeSearchQuery(e.target.value);
    setChallengeCurrentPage(1);
  };

  const handleChallengePageChange = (newPage) => {
    setChallengeCurrentPage(newPage);
  };

  // Existing workshop click handler (unchanged)
  const handleWorkshopClick = (
    workshopSlug,
    workshopExperts,
    workshopPurchaseId
  ) => {
    const expertIds = workshopExperts.map((expert) => expert.id);
    localStorage.setItem("expertIds", JSON.stringify(expertIds));
    navigate(`/my-learning/ondemand/${workshopPurchaseId}/${workshopSlug}`);
  };

  // New challenge click handler
  const handleChallengeClick = (challengeSlug, challengePurchaseId) => {
    navigate(`/my-learning/challenge/${challengePurchaseId}/${challengeSlug}`);
  };

  // Existing workshop join handler (unchanged)
  const handleJoinNowButtonClick = (live_workshop_url) => {
    if (live_workshop_url) {
      window.open(live_workshop_url, "_blank");
    } else {
      setShowAlert(true);
      handleAddAlert(
        "Live Workshop Not Available",
        "The workshop has not started yet, or the link is not available. If the issue persists, please contact support.",
        "error",
        true
      );
    }
  };

  const handleAddAlert = (title, message, type, autoClose = false) => {
    addAlert(title, message, type, autoClose, setAlerts);
  };

  const handleRemoveAlert = (id) => {
    removeAlert(id, setAlerts);
  };

  // Get current tab data
  const currentItems = activeTab === "workshops" ? workshops : challenges;
  const currentTotalPages =
    activeTab === "workshops" ? totalPages : challengeTotalPages;
  const currentPageNumber =
    activeTab === "workshops" ? currentPage : challengeCurrentPage;
  const currentSearchQuery =
    activeTab === "workshops" ? searchQuery : challengeSearchQuery;

  return (
    <div className="my-learning-container">
      {/* Tab Navigation */}
      <div className="tab-navigation">
        <button
          className={`tab-button ${activeTab === "workshops" ? "active" : ""}`}
          onClick={() => handleTabChange("workshops")}
        >
          My Workshops
        </button>
        <button
          className={`tab-button ${activeTab === "challenges" ? "active" : ""}`}
          onClick={() => handleTabChange("challenges")}
        >
          My Challenges
        </button>
      </div>

      <div className="my-learning-header">
        <div className="controls">
          <div className="sort-filter">
            {/* Only show sort options for workshops */}
            {activeTab === "workshops" && (
              <div className="sort-filter-container">
                <p>Sort by:</p>
                <select value={sortOption || ""} onChange={handleSortChange}>
                  <option value="recently_purchased">Recently purchased</option>
                  <option value="alphabetical">Alphabetical</option>
                  <option value="upcoming">Upcoming</option>
                  <option value="duration">Duration</option>
                </select>
              </div>
            )}
            {/* Only show filter options for workshops */}
            {activeTab === "workshops" && (
              <div className="sort-filter-container">
                <p>Filter by:</p>
                <select value={filterOption} onChange={handleFilterChange}>
                  <option value="">All</option>
                  <option value="ondemand">Recorded</option>
                  <option value="live">Live Schedule</option>
                </select>
              </div>
            )}
          </div>
          <div className="search">
            <input
              type="text"
              placeholder={`Search ${activeTab === "workshops" ? "courses" : "challenges"}`}
              value={currentSearchQuery}
              onChange={
                activeTab === "workshops"
                  ? handleSearchChange
                  : handleChallengeSearchChange
              }
            />
          </div>
        </div>
      </div>

      {currentItems.length === 0 ? (
        <NoPackagesContainer>
          <NoPackagesImage
            src={emptyBox}
            alt={`You have not yet purchased any ${activeTab}`}
          />
          <NoPackagesText>
            You have not yet purchased any {activeTab}
          </NoPackagesText>
        </NoPackagesContainer>
      ) : (
        <>
          <div className="cards-container">
            {activeTab === "workshops"
              ? // Existing workshop cards (unchanged)
                workshops?.map((item) => {
                  const isLiveSchedule =
                    item.workshop.type.title.toLowerCase() === "live schedule";
                  const currentDateTime = new Date();
                  const startDateTime = new Date(
                    item?.workshop_batch?.start_date_time
                      ? item?.workshop_batch?.start_date_time
                      : item?.workshop?.start_date_time
                  );
                  const endDateTime = new Date(
                    item?.workshop_batch?.end_date_time
                      ? item?.workshop_batch?.end_date_time
                      : item?.workshop?.end_date_time
                  );
                  const displayBatchName = item?.workshop_batch?.display_name
                    ? item?.workshop_batch?.display_name
                    : "";
                  const isJoinDisabled =
                    isLiveSchedule && endDateTime < currentDateTime;
                  const formattedDate = startDateTime.toLocaleDateString(
                    "en-GB",
                    {
                      day: "2-digit",
                      month: "2-digit",
                      year: "numeric",
                    }
                  );
                  const formattedTime = startDateTime.toLocaleTimeString(
                    "en-US",
                    {
                      hour: "numeric",
                      minute: "2-digit",
                      hour12: true,
                    }
                  );

                  return (
                    <div
                      key={item?.id}
                      className="mylearning-card"
                      onClick={() =>
                        handleWorkshopClick(
                          item?.workshop?.slug,
                          item?.workshop?.workshop_experts,
                          item?.id
                        )
                      }
                    >
                      <div
                        className={`mylearning-card-chip ${item?.workshop?.type?.title.toLowerCase().replace(/\s+/g, "-")}`}
                      >
                        {item?.workshop?.type?.title === "Live Schedule" ? (
                          <span>Live</span>
                        ) : (
                          <span>Recorded</span>
                        )}
                      </div>
                      <img
                        src={
                          item?.workshop?.file || "/path-to-placeholder-image"
                        }
                        alt={item?.workshop?.title}
                        className="card-image"
                      />
                      <div className="card-content">
                        <div
                          className={`card-category-wrapper ${!isLiveSchedule ? "recorded" : ""}`}
                        >
                          <div className="card-details-mylearning">
                            <h3 className="card-title">
                              {item?.workshop?.title}
                            </h3>

                            <p className="card-expert">
                              by{" "}
                              {item?.workshop?.workshop_experts
                                .map(
                                  (expert) =>
                                    `${expert.first_name} ${expert.last_name}`
                                )
                                .join(", ")}
                            </p>

                            {isLiveSchedule ? (
                              <>
                                <p className="card-category">
                                  Start date: {formattedDate}
                                  <br />
                                  Start time: {formattedTime}
                                  <br />
                                  {displayBatchName != ""
                                    ? `Batch: ${displayBatchName}`
                                    : ""}
                                </p>

                                <button
                                  className="join-button"
                                  onClick={() =>
                                    handleWorkshopClick(
                                      item?.workshop?.slug,
                                      item?.workshop?.workshop_experts,
                                      item?.id
                                    )
                                  }
                                >
                                  Access Workshop
                                </button>

                                {!isJoinDisabled &&
                                  item?.workshop?.live_workshop_url && (
                                    <button
                                      className="join-button"
                                      onClick={() =>
                                        handleJoinNowButtonClick(
                                          item?.workshop?.live_workshop_url
                                        )
                                      }
                                    >
                                      Join Live Workshop
                                    </button>
                                  )}

                                <div className="whatsapp-button-workshopcard-wrapper">
                                  {item?.workshop?.workshop_batch
                                    ? item?.workshop_batch
                                        ?.whatsapp_group_url && (
                                        <WhatsAppButton
                                          groupLink={
                                            item?.workshop_batch
                                              ?.whatsapp_group_url
                                          }
                                        />
                                      )
                                    : item?.workshop?.whatsapp_group_url && (
                                        <WhatsAppButton
                                          groupLink={
                                            item?.workshop?.whatsapp_group_url
                                          }
                                        />
                                      )}
                                </div>
                              </>
                            ) : (
                              <>
                                <button
                                  className="join-button"
                                  onClick={() =>
                                    handleWorkshopClick(
                                      item?.workshop?.slug,
                                      item?.workshop?.workshop_experts,
                                      item?.id
                                    )
                                  }
                                >
                                  Access Workshop
                                </button>

                                <div className="whatsapp-button-workshopcard-wrapper">
                                  {item?.workshop?.workshop_batch
                                    ? item?.workshop_batch
                                        ?.whatsapp_group_url && (
                                        <WhatsAppButton
                                          groupLink={
                                            item?.workshop_batch
                                              ?.whatsapp_group_url
                                          }
                                        />
                                      )
                                    : item?.workshop?.whatsapp_group_url && (
                                        <WhatsAppButton
                                          groupLink={
                                            item?.workshop?.whatsapp_group_url
                                          }
                                        />
                                      )}
                                </div>
                              </>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })
              : // Challenge cards (no sorting)
                challenges?.map((item) => (
                  <ChallengeCard
                    key={item?.id}
                    challenge={item}
                    onChallengeClick={handleChallengeClick}
                  />
                ))}
          </div>

          <div className="pagination">
            {Array.from({ length: currentTotalPages }).map((_, index) => (
              <button
                key={index + 1}
                className={currentPageNumber === index + 1 ? "active" : ""}
                onClick={() =>
                  activeTab === "workshops"
                    ? handlePageChange(index + 1)
                    : handleChallengePageChange(index + 1)
                }
              >
                {index + 1}
              </button>
            ))}
          </div>
        </>
      )}

      {showAlert &&
        alerts.map((alert) => (
          <CustomAlert
            key={alert.id}
            title={alert.title}
            message={alert.message}
            onClose={() => handleRemoveAlert(alert.id)}
            autoClose={alert.autoClose}
            type={alert.type}
          />
        ))}
    </div>
  );
};

export default MyLearning;
